<template>
	<div class="follow-up" data-test-id="questionnaire-follow-up">
		<div class="follow-up__header">
			<span class="follow-up__title">{{ t.title }} {{ follow_up_index + 1 }}</span>
			<label class="labeled-field labeled-field--select follow-up__range-field">
				<span class="labeled-field__label">{{ t.from }}</span>
				<div class="labeled-field__input-wrap">
					<select
						class="labeled-field__input"
						data-test-id="questionnaire-follow-up-from"
						:value="firstAnswerId"
						:disabled="!follow_up.update_url"
						@change="updateBoundary('first', $event.target.value)"
					>
						<option :value="null">—</option>
						<option v-for="answer in rangeAnswers" :key="'from.' + answer.id" :value="answer.id" :disabled="isTaken(answer)">{{ answer.title }}</option>
					</select>
				</div>
			</label>
			<label class="labeled-field labeled-field--select follow-up__range-field">
				<span class="labeled-field__label">{{ t.to }}</span>
				<div class="labeled-field__input-wrap">
					<select
						class="labeled-field__input"
						data-test-id="questionnaire-follow-up-to"
						:value="lastAnswerId"
						:disabled="!follow_up.update_url"
						@change="updateBoundary('last', $event.target.value)"
					>
						<option :value="null">—</option>
						<option v-for="answer in rangeAnswers" :key="'to.' + answer.id" :value="answer.id" :disabled="isTaken(answer)">{{ answer.title }}</option>
					</select>
				</div>
			</label>
			<button class="link-btn link-btn--red ml-auto" type="button" :disabled="!follow_up.update_url" @click.prevent="remove">
				{{ t.remove }}
			</button>
		</div>

		<p v-if="state == 'conflict'" class="follow-up__hint follow-up__hint--error" data-test-id="questionnaire-follow-up-conflict-hint">
			{{ t.range_conflict_hint }}
		</p>
		<p v-if="followUpAnswers.length == 0" class="follow-up__hint" data-test-id="questionnaire-follow-up-range-hint">
			{{ t.empty_range_hint }}
		</p>
		<p v-else-if="activeQuestions.length == 0" class="follow-up__hint" data-test-id="questionnaire-follow-up-question-hint">
			{{ t.empty_question_hint }}
		</p>

		<div class="follow-up__questions">
			<template v-for="(dependent, dependent_index) in follow_up.questions">
				<QuestionnairesQuestion
					v-if="!dependent._destroy"
					:key="'question.' + question_index + '.follow_up.' + follow_up_index + '.question.' + dependent_index"
					:question="dependent"
					:question_index="dependent_index"
					:nested="true"
				/>
			</template>
			<button class="link-btn" type="button" data-test-id="questionnaire-add-dependent-question-btn" :disabled="!follow_up.update_url" @click.prevent="addQuestion">
				{{ t.add_question }}
			</button>
		</div>
	</div>
</template>

<script>
import { defineAsyncComponent, reactive } from 'vue';
import request from './request.js';

export default {
	name: 'QuestionnairesFollowUp',

	components: {
		// Вопрос содержит уточнения, уточнение содержит вопросы: асинхронный импорт разрывает цикл модулей
		QuestionnairesQuestion: defineAsyncComponent(() => import('./Question.vue'))
	},

	props: {
		follow_up: { type: Object, required: true },
		// Adaptive-вопрос, варианты которого образуют ветку
		question: { type: Object, required: true },
		question_index: { type: Number, required: true },
		follow_up_index: { type: Number, required: true }
	},

	data() {
		return {
			state: '',
			// Номер последнего запроса границ: ответы на более ранние запросы устарели
			range_request: 0,
			t: I18n.t.questionnaire_dashboard_form.fields.questionnaire.questions.follow_ups
		}
	},

	computed: {
		// Варианты, доступные для границ ветки, в порядке показа
		rangeAnswers() {
			return this.question.answers.filter(answer => !answer._destroy && answer.id).sort((a, b) => a.pos - b.pos);
		},

		followUpAnswers() {
			return this.rangeAnswers.filter(answer => answer.questionnaire_follow_up_id == this.follow_up.id);
		},

		firstAnswerId() {
			return this.followUpAnswers.length > 0 ? this.followUpAnswers[0].id : null;
		},

		lastAnswerId() {
			return this.followUpAnswers.length > 0 ? this.followUpAnswers[this.followUpAnswers.length - 1].id : null;
		},

		activeQuestions() {
			return this.follow_up.questions.filter(dependent => !dependent._destroy);
		},
	},

	methods: {
		// Вариант уже входит в другую ветку этого вопроса
		isTaken(answer) {
			return !!answer.questionnaire_follow_up_id && answer.questionnaire_follow_up_id != this.follow_up.id;
		},

		// Границы задают непрерывный отрезок вариантов; отрезок, пересекающий соседнюю ветку, сервер отклоняет.
		// Пустое значение «—» в любой границе снимает диапазон целиком
		updateBoundary(boundary, answer_id) {
			const answers = this.rangeAnswers;
			const changed_index = answers.findIndex(answer => answer.id == answer_id);
			const other_id = boundary == 'first' ? this.lastAnswerId : this.firstAnswerId;
			const other_index = answers.findIndex(answer => answer.id == other_id);
			let answer_ids = [];

			if (changed_index >= 0) {
				const bounds = other_index < 0 ? [changed_index] : [changed_index, other_index];
				answer_ids = answers.slice(Math.min(...bounds), Math.max(...bounds) + 1).map(answer => answer.id);
			}

			// Сразу локально: вторая граница читает первую, не дожидаясь ответа сервера
			const previous_ids = this.followUpAnswers.map(answer => answer.id);
			this.applyAnswers(answer_ids);
			const request_number = ++this.range_request;

			request('PATCH', this.follow_up.update_url, { follow_up: { answer_ids } }).then(response => {
				// Ответы могут прийти не по порядку: состояние берём только из последнего запроса
				if (request_number != this.range_request) return;

				this.applyAnswers(response.data.follow_up.answer_ids);
				this.state = 'synced';
			}).catch(error => {
				if (request_number != this.range_request) return;

				// Отклонённый диапазон откатывается до прежнего состава: селекты привязаны к нему
				this.applyAnswers(previous_ids);
				this.state = error.response && error.response.status == 403 ? 'conflict' : 'error';
			})
		},

		// Единственный источник истины о составе ветки: ссылка на неё у вариантов вопроса.
		// Чужие варианты не трогаем: при пересечении сервер ответит отказом и состав откатится
		applyAnswers(answer_ids) {
			this.question.answers.forEach(answer => {
				if (answer_ids.includes(answer.id)) {
					if (!this.isTaken(answer)) answer.questionnaire_follow_up_id = this.follow_up.id;
				} else if (answer.questionnaire_follow_up_id == this.follow_up.id) {
					answer.questionnaire_follow_up_id = null;
				}
			});
		},

		remove() {
			if (this.activeQuestions.length > 0 && !window.confirm(this.t.remove_confirm)) return;

			this.follow_up._destroy = true;

			request('DELETE', this.follow_up.update_url, {}).then(response => {
				this.applyAnswers([]);
				this.state = 'synced';
			}).catch(errors => {
				this.follow_up._destroy = false;
				this.state = 'error';
			})
		},

		addQuestion() {
			// reactive: адреса из ответа сервера должны разблокировать кнопки и запустить отложенное сохранение
			let dependent = reactive({
				title: '', question_type: 'one', required: true, _destroy: false, answers: [], follow_ups: [],
				questionnaire_follow_up_id: this.follow_up.id,
				pos: Math.max(0, ...this.activeQuestions.map(q => q.pos)) + 1
			});
			this.follow_up.questions = this.follow_up.questions.filter(q => !q._destroy);
			this.follow_up.questions.push(dependent);

			request('POST', this.follow_up.create_question_url, { question: dependent }).then(response => {
				dependent.id = response.data.question.id;
				dependent.update_url = response.data.question.update_url;
				dependent.create_answer_url = response.data.question.create_answer_url;
				this.state = 'synced';
			}).catch(errors => {
				this.state = 'error';
			})
		},
	},
}
</script>
